/**
 * About Sustainow content aligned with sustainow.in (mission, solutions, awards, hackathon).
 * @see https://sustainow.in
 */

export const ABOUT_US_PAGE = {
  title: 'About Sustainow',
  tagline: 'Sustainability, Starting Now',
  quote: {
    text: 'The greatest threat to our planet is the belief that someone else will save it.',
    attribution: 'Robert Swan',
  },
  contactEmail: 'contact@sustainow.in',
  websiteUrl: 'https://sustainow.in',
  websiteLabel: 'sustainow.in',
};

export const PATENT_PENDING_HIGHLIGHT = {
  label: 'Patent pending',
  description:
    'Our carbon intelligence workflow—linking activity data, verified emission factors, AI-assisted analytics, and disclosure-ready reporting—is protected as a patent-pending innovation developed for Indian MSME operating realities.',
};

export const ABOUT_MISSION = {
  title: 'Our mission',
  paragraphs: [
    'At Sustainow, our mission is to protect and preserve the environment for future generations. We work to reduce greenhouse gas emissions, conserve natural resources, and promote sustainable living practices through practical technology.',
    'We build carbon intelligence for Indian MSMEs—single-site manufacturers, processors, and service units that need credible footprint data without a full in-house ESG team.',
  ],
};

export const ABOUT_HISTORY = {
  title: 'Our history',
  paragraphs: [
    'Founded in 2025, Sustainow was created in response to growing environmental and disclosure pressures on small and growing businesses. Since then, we have focused on making a positive impact through AI-enabled products, research, and community engagement.',
    'The Sustainow Carbon Intelligence solution extends that mission into day-to-day operations: document capture, scope 1–3 assessment, compliance-oriented reporting, and green-finance readiness.',
  ],
};

export const ABOUT_AWARDS = {
  title: 'Recognition & incubation',
  items: [
    {
      title: 'RBIH GreenFinTech Hackathon 2024',
      description:
        'Winner of the GreenFinTech Hackathon hosted by the Reserve Bank Innovation Hub (RBIH)—the innovation arm of the Reserve Bank of India—recognising our approach to MSME carbon footprint measurement and green finance enablement.',
    },
    {
      title: 'IIITB Innovation Center',
      description:
        'Incubated at the IIIT Bangalore Innovation Center, supporting product development, validation, and scale-up of climate and sustainability tooling for Indian enterprises.',
    },
    {
      title: 'MeitY TIDE 2.0 Grant',
      description:
        'Recipient of the MeitY TIDE 2.0 grant programme, backing technology innovation and entrepreneurship in domains that strengthen India’s digital and sustainability ecosystem.',
    },
  ],
};

export const ABOUT_TEAM = {
  title: 'Our team',
  paragraphs: [
    'Our team is made up of passionate research and development professionals committed to environmental protection and accessible climate tooling for businesses.',
    'We bring together expertise in sustainable agriculture, renewable energy, environmental products, and software engineering—working as one group to deliver measurable outcomes for MSMEs and their supply-chain partners.',
  ],
};

export const CARBON_INTELLIGENCE_SOLUTION = {
  title: 'Sustainow Carbon Intelligence solution',
  platformSummary:
    'Sustainow is an AI-powered platform that combines climate data and ESG analytics to help businesses measure risk, improve sustainability, and make smarter decisions—with Carbon Intelligence tailored for Indian MSMEs.',
  lead:
    'Carbon Intelligence, built for MSMEs: measure sustainability from intent through operations, identify reduction opportunities, and report progress with confidence.',
  paragraphs: [
    'We help small and medium enterprises measure emissions, find hotspots in energy, materials, and logistics, and respond to buyers, lenders, and regulators with structured evidence.',
    'Our goal is to make carbon accounting accessible so every MSME can compete in low-carbon supply chains and unlock green financing—a future where sustainability reporting is a normal business advantage, not a barrier.',
  ],
  capabilities: [
    {
      title: 'Carbon footprint & green finance',
      description:
        'Scope 1–3 assessment, document and transaction capture, period analytics, and reporting outputs aligned with BRSR, ISO-oriented disclosures, and lender or buyer evidence packs.',
    },
    {
      title: 'Climate risk',
      description:
        'AI-powered insights to understand and manage climate-related risk for Indian operating contexts—linking emissions, resource use, and business continuity.',
    },
    {
      title: 'Sustainability innovation',
      description:
        '“Innovating today to protect tomorrow”—frameworks to generate and prioritise sustainability improvements across products, processes, and value chains.',
    },
    {
      title: 'Green workforce skills',
      description:
        'We don’t just measure carbon—we build the workforce that manages it. India-focused curriculum for MSMEs, graduates, and professionals aligned with ESG, carbon markets, BRSR, and industrial decarbonisation.',
    },
  ],
};

export type SustainowSolutionItem = {
  title: string;
  description: string;
  url?: string;
  urlLabel?: string;
};

export const SUSTAINOW_SOLUTIONS_SUITE: {
  title: string;
  intro: string;
  items: SustainowSolutionItem[];
} = {
  title: 'Broader Sustainow solutions',
  intro:
    'Beyond Carbon Intelligence, Sustainow develops environmental and innovation systems across the Sustainow network:',
  items: [
    {
      title: 'Klima',
      url: 'https://klima.sustainow.in',
      urlLabel: 'klima.sustainow.in',
      description:
        'Industrial asset climate risk—map a location, read risk scores, and review MOSDAC satellite imagery for India-focused site intelligence.',
    },
    {
      title: 'KlimaGuard Kids',
      url: 'https://klimaguardkids.sustainow.in',
      urlLabel: 'klimaguardkids.sustainow.in',
      description:
        'Child-centric climate health intelligence—agentic AI linking climate, health, nutrition, and disease risk across India’s regional climate zones.',
    },
    {
      title: 'JalAgent',
      url: 'https://jalagent.sustainow.in',
      urlLabel: 'jalagent.sustainow.in',
      description:
        'Water intelligence agent for conservation, stewardship, and operational water decisions—extending Sustainow’s resource-efficiency work.',
    },
    {
      title: 'Climate risk systems',
      description:
        'Tools to measure and communicate climate risk for Indian demography and infrastructure—supporting proactive planning as risks grow in frequency and intensity.',
    },
    {
      title: 'Sustainability Innovation Manager',
      description:
        'Systems to generate and evaluate sustainability-led ideas for B2B companies—balancing environmental outcomes with long-term economic resilience.',
    },
    {
      title: 'Water & resource innovation',
      description:
        'Initiatives such as rH20 (water recycling) and Aqua Conserve toilet valve retrofits that convert conventional fixtures into water-efficient installations.',
    },
  ],
};

/** Shorter copy for the pre-login dashboard About section (links to full page). */
export const ABOUT_US_SECTION = {
  title: 'About us',
  lead: ABOUT_MISSION.paragraphs[1],
  paragraphs: [
    CARBON_INTELLIGENCE_SOLUTION.paragraphs[0],
    'Learn more about our mission, RBIH GreenFinTech Hackathon recognition, patent-pending carbon intelligence workflow, and the full solution suite on sustainow.in.',
  ],
  contactEmail: ABOUT_US_PAGE.contactEmail,
};
